/**
 * Family Inbox — intelligent communication command center for the family.
 * Apple Mail × Notion × Slack × Banking Notifications, not WhatsApp.
 *
 * Premium redesign with ambient hero, glassmorphic AI summary,
 * priority-coded attention cards, visual timeline, and rich conversation cards.
 */

import React, { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { TriSpherePayTopUp } from "@/components/schoolos-pay-topup";
import {
  Send, Search, Plus, X, Phone, Video,
  CheckCheck, Clock, Paperclip, Star, Trash2,
  Sparkles, Zap, ArrowLeft, FileText,
  ChevronDown, Bell, Download, Reply,
  AlertCircle, CheckCircle2,
  Users, BookOpen, CreditCard, Bus, Shield, Award,
  ArrowRight, AlertTriangle, TrendingUp, Inbox as InboxIcon,
  Heart, Settings, Hash, Pin, Filter, MoreHorizontal,
  Sun, Moon, Coffee, Droplets, Wind, CloudRain,
  MessageSquare, AtSign, Bookmark,
  PenTool, Check, X as XIcon, Image as ImageIcon, File as FileIcon,
} from "lucide-react";

// ─── Apple design tokens ────────────────────────────────────────────────────
const C = {
  text: "#1C1917", textMid: "#3C3530", textMuted: "#8B6F47", textLight: "#A68A5A",
  bg: "#EDE4D4", card: "#FFFFFF", border: "#E2D6C0", cardBorder: "#E2D6C0",
  accent: "#7B1416", accentLight: "#F5EFE4",
  green: "#C4A882", greenLight: "#E3F9E5",
  red: "#B33A1A", redLight: "#FEE5E5",
  orange: "#C4A882", orangeLight: "#FFF4E5",
  teal: "#C4A882", tealLight: "#E6FFFE",
  purple: "#8B6F47", purpleLight: "#F5E6FD",
  pink: "#FF2D55", pinkLight: "#FFE8ED",
  blue: "#7B1416", blueLight: "#F5EFE4",
  gold: "#FFCC00", goldLight: "#FFFADC",
  amber: "#C4A882", amberLight: "#FFF4E5",
} as const;

const EASE = [0.4, 0, 0.2, 1] as const;
const springTransition = { type: "spring" as const, stiffness: 400, damping: 30 };
const INBOX_GRADIENT = "linear-gradient(135deg, #8B6F47 0%, #6366f1 100%)";

// ─── Types ──────────────────────────────────────────────────────────────────
type Message = {
  id: string; thread_id: string; sender_id: string; sender_name: string;
  sender_role: string; recipient_id: string | null; student_id: string | null;
  subject: string | null; body: string; read_at: string | null; created_at: string;
  status?: "sending" | "sent" | "delivered" | "read" | "failed";
  priority?: "normal" | "important" | "urgent";
  starred?: boolean;
  attachments?: { name: string; type: string; size: string; url: string }[];
  reactions?: { emoji: string; count: number; reacted: boolean }[];
};

type Thread = {
  thread_id: string; sender_name: string; sender_role: string;
  subject: string | null; last_body: string; last_at: string;
  unread: boolean; messages: Message[];
  starred?: boolean; pinned?: boolean; muted?: boolean; archived?: boolean;
  category?: "inbox" | "academic" | "finance" | "health" | "transport" | "announcements";
  priority?: "normal" | "important" | "urgent";
  typing?: boolean;
};

type Contact = { id: string; name: string; role: string; department: string; online: boolean };

const CONTACTS: Contact[] = [
  { id: "1", name: "Mr. Okello", role: "teacher", department: "Science", online: true },
  { id: "2", name: "Nurse Namusoke", role: "nurse", department: "Health", online: false },
  { id: "3", name: "Finance Office", role: "admin", department: "Finance", online: true },
  { id: "4", name: "Mrs. Komuhangi", role: "teacher", department: "Mathematics", online: false },
  { id: "5", name: "Headmaster", role: "admin", department: "Administration", online: false },
];

const ROLE_CONFIG: Record<string, { color: string; bg: string; label: string; icon: React.ComponentType<{ className?: string }> }> = {
  teacher: { color: C.blue, bg: C.blueLight, label: "Teacher", icon: BookOpen },
  admin: { color: C.purple, bg: C.purpleLight, label: "Administration", icon: Settings },
  nurse: { color: C.green, bg: C.greenLight, label: "Health Center", icon: Heart },
  parent: { color: C.orange, bg: C.orangeLight, label: "You", icon: Users },
  finance: { color: C.teal, bg: C.tealLight, label: "Finance", icon: CreditCard },
};

const CATEGORY_CONFIG: Record<string, { color: string; bg: string; icon: React.ComponentType<{ className?: string }> }> = {
  academic: { color: C.blue, bg: C.blueLight, icon: BookOpen },
  finance: { color: C.teal, bg: C.tealLight, icon: CreditCard },
  health: { color: C.green, bg: C.greenLight, icon: Heart },
  transport: { color: C.orange, bg: C.orangeLight, icon: Bus },
  announcements: { color: C.purple, bg: C.purpleLight, icon: Bell },
  inbox: { color: C.accent, bg: C.accentLight, icon: InboxIcon },
};

const DEMO_THREADS: Thread[] = [
  {
    thread_id: "thread-1", sender_name: "Mr. Okello", sender_role: "teacher",
    subject: "Science Project — Friday Deadline",
    last_body: "The science project is due this Friday. Please ensure your child brings all lab materials.",
    last_at: new Date(Date.now() - 7200000).toISOString(), unread: true, pinned: true,
    category: "academic", priority: "important",
    messages: [
      { id: "msg-1", thread_id: "thread-1", sender_id: "teacher-1", sender_name: "Mr. Okello", sender_role: "teacher", recipient_id: null, student_id: null, subject: "Science Project — Friday Deadline", body: "Good afternoon! The science project is due this Friday. Please ensure your child brings all lab materials and the written report.", read_at: null, created_at: new Date(Date.now() - 7200000).toISOString(), status: "read", priority: "important", attachments: [{ name: "Science_Project_Guidelines.pdf", type: "pdf", size: "245 KB", url: "#" }, { name: "Marking_Rubric.docx", type: "doc", size: "128 KB", url: "#" }], reactions: [{ emoji: "👍", count: 1, reacted: false }] },
      { id: "msg-2", thread_id: "thread-1", sender_id: "teacher-1", sender_name: "Mr. Okello", sender_role: "teacher", recipient_id: null, student_id: null, subject: null, body: "Emma has been doing great — her participation has been excellent this term. Keep up the great support at home!", read_at: null, created_at: new Date(Date.now() - 3600000).toISOString(), status: "read", reactions: [{ emoji: "❤️", count: 1, reacted: true }] },
    ],
  },
  {
    thread_id: "thread-2", sender_name: "Finance Office", sender_role: "admin",
    subject: "Term 3 Fee Reminder",
    last_body: "Term 3 fees are due by July 15th. Please ensure full payment to avoid late penalties.",
    last_at: new Date(Date.now() - 86400000).toISOString(), unread: true, starred: true,
    category: "finance", priority: "urgent",
    messages: [
      { id: "msg-3", thread_id: "thread-2", sender_id: "admin-1", sender_name: "Finance Office", sender_role: "admin", recipient_id: null, student_id: null, subject: "Term 3 Fee Reminder", body: "This is a reminder that Term 3 fees are due by July 15th. Total: UGX 1,730,000. Payment reference: EM-2025-T3.", read_at: null, created_at: new Date(Date.now() - 86400000).toISOString(), status: "read", priority: "urgent", attachments: [{ name: "Fee_Invoice_T3.pdf", type: "pdf", size: "156 KB", url: "#" }] },
    ],
  },
  {
    thread_id: "thread-3", sender_name: "Nurse Namusoke", sender_role: "nurse",
    subject: "Health Update — Emma",
    last_body: "Emma visited the clinic today with a mild headache. She has fully recovered.",
    last_at: new Date(Date.now() - 172800000).toISOString(), unread: false,
    category: "health", priority: "normal",
    messages: [
      { id: "msg-4", thread_id: "thread-3", sender_id: "nurse-1", sender_name: "Nurse Namusoke", sender_role: "nurse", recipient_id: null, student_id: null, subject: "Health Update — Emma", body: "Emma visited the clinic today at 10:30 AM with a mild headache. She was given paracetamol and rested for 45 minutes. She has fully recovered and returned to class.", read_at: new Date(Date.now() - 172000000).toISOString(), created_at: new Date(Date.now() - 172800000).toISOString(), status: "read" },
    ],
  },
  {
    thread_id: "thread-4", sender_name: "Mrs. Komuhangi", sender_role: "teacher",
    subject: "Math Olympiad Selection",
    last_body: "Emma has been selected to represent the school in the National Math Olympiad!",
    last_at: new Date(Date.now() - 259200000).toISOString(), unread: false, starred: true,
    category: "academic", priority: "important",
    messages: [
      { id: "msg-5", thread_id: "thread-4", sender_id: "teacher-2", sender_name: "Mrs. Komuhangi", sender_role: "teacher", recipient_id: null, student_id: null, subject: "Math Olympiad Selection", body: "Wonderful news! Emma has been selected to represent our school in the National Math Olympiad scheduled for August 2025. Preparation sessions will begin next month.", read_at: new Date(Date.now() - 259100000).toISOString(), created_at: new Date(Date.now() - 259200000).toISOString(), status: "read", priority: "important", reactions: [{ emoji: "🎉", count: 2, reacted: true }, { emoji: "👏", count: 1, reacted: false }] },
    ],
  },
  {
    thread_id: "thread-5", sender_name: "Headmaster's Office", sender_role: "admin",
    subject: "Open Day 2025 Invitation",
    last_body: "You are cordially invited to our annual Open Day celebration on July 19th.",
    last_at: new Date(Date.now() - 432000000).toISOString(), unread: false,
    category: "announcements", priority: "normal",
    messages: [
      { id: "msg-6", thread_id: "thread-5", sender_id: "admin-2", sender_name: "Headmaster's Office", sender_role: "admin", recipient_id: null, student_id: null, subject: "Open Day 2025 Invitation", body: "Dear Parent, you are cordially invited to our annual Open Day celebration on Saturday, July 19th, 2025. Program: 9:00 AM Opening, 10:00 AM Exhibitions, 12:00 PM Performances, 2:00 PM Awards.", read_at: new Date(Date.now() - 431000000).toISOString(), created_at: new Date(Date.now() - 432000000).toISOString(), status: "read", attachments: [{ name: "OpenDay_Program.pdf", type: "pdf", size: "320 KB", url: "#" }] },
    ],
  },
];

// ─── Helpers ────────────────────────────────────────────────────────────────
function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-UG", { month: "short", day: "numeric" });
}
function formatTime(iso: string) { return new Date(iso).toLocaleTimeString("en-UG", { hour: "2-digit", minute: "2-digit" }); }

// ─── Avatar ─────────────────────────────────────────────────────────────────
function Avatar({ name, role, size = 40, online = false }: { name: string; role: string; size?: number; online?: boolean }) {
  const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const config = ROLE_CONFIG[role] ?? ROLE_CONFIG.teacher;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <div className="relative flex items-center justify-center rounded-2xl text-white font-semibold" style={{ width: size, height: size, background: `linear-gradient(135deg, ${config.color}, ${config.color}cc)`, fontSize: size * 0.35 }}>
        {initials}
      </div>
      {online && <div className="absolute rounded-full ring-2 ring-white" style={{ width: size * 0.28, height: size * 0.28, bottom: 0, right: 0, background: C.green }} />}
    </div>
  );
}

// ─── Message Bubble ──────────────────────────────────────────────────────────
function MessageBubble({ msg, isMe, showAvatar }: { msg: Message; isMe: boolean; showAvatar: boolean }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={springTransition}
      className={`flex items-end gap-2.5 ${isMe ? "flex-row-reverse" : "flex-row"}`}>
      {showAvatar && !isMe && <Avatar name={msg.sender_name} role={msg.sender_role} size={32} />}
      {showAvatar && isMe && <div className="w-8" />}
      <div className={`max-w-[75%] flex flex-col ${isMe ? "items-end" : "items-start"}`}>
        {!isMe && showAvatar && <span className="text-xs font-semibold mb-1 ml-1" style={{ color: C.text }}>{msg.sender_name}</span>}
        <div className="px-4 py-3 rounded-2xl" style={{ background: isMe ? C.accent : C.card, color: isMe ? "white" : C.text, borderBottomLeftRadius: !isMe ? 4 : 16, borderBottomRightRadius: isMe ? 4 : 16, border: isMe ? "none" : `1px solid ${C.border}` }}>
          <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.body}</p>
          {msg.attachments && msg.attachments.length > 0 && (
            <div className="mt-3 space-y-2">
              {msg.attachments.map((att, i) => (
                <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-xl" style={{ background: isMe ? "rgba(255,255,255,0.15)" : C.bg }}>
                  <FileText className="w-5 h-5 shrink-0" style={{ color: isMe ? "white" : C.textMuted }} />
                  <div className="flex-1 min-w-0"><p className="text-xs font-medium truncate" style={{ color: isMe ? "white" : C.text }}>{att.name}</p><p className="text-[10px]" style={{ color: isMe ? "rgba(255,255,255,0.7)" : C.textMuted }}>{att.size}</p></div>
                  <Download className="w-4 h-4 shrink-0" style={{ color: isMe ? "white" : C.textMuted }} />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className={`flex items-center gap-1.5 mt-1.5 px-1 ${isMe ? "flex-row-reverse" : ""}`}>
          <span className="text-[10px]" style={{ color: C.textMuted }}>{formatTime(msg.created_at)}</span>
          {isMe && msg.status === "read" && <CheckCheck className="w-3 h-3" style={{ color: C.accent }} />}
          {isMe && msg.status === "delivered" && <CheckCheck className="w-3 h-3" style={{ color: C.textMuted }} />}
          {isMe && msg.status === "sending" && <Clock className="w-3 h-3" style={{ color: C.textMuted }} />}
        </div>
      </div>
    </motion.div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
export function ParentMessages() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [activeThread, setActiveThread] = useState<Thread | null>(null);
  const [search, setSearch] = useState("");
  const [reply, setReply] = useState("");
  const [composing, setComposing] = useState(false);
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [composeTo, setComposeTo] = useState("");
  const [threads, setThreads] = useState<Thread[]>(DEMO_THREADS);
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [hoveredThread, setHoveredThread] = useState<string | null>(null);

  // Fetch messages
  const { data: dbMessages = [] } = useQuery<Message[]>({
    queryKey: ["parent_messages", user?.id], enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase.from("parent_messages").select("*").or(`sender_id.eq.${user!.id},recipient_id.eq.${user!.id}`).order("created_at", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

  // Merge DB messages
  useEffect(() => {
    if (dbMessages.length === 0) return;
    const threadMap = new Map<string, Thread>();
    dbMessages.forEach((msg) => {
      if (!threadMap.has(msg.thread_id)) {
        threadMap.set(msg.thread_id, { thread_id: msg.thread_id, sender_name: msg.sender_role === "parent" ? "You" : msg.sender_name, sender_role: msg.sender_role, subject: msg.subject, last_body: msg.body, last_at: msg.created_at, unread: msg.sender_role !== "parent" && !msg.read_at, messages: [] });
      }
      const thread = threadMap.get(msg.thread_id)!;
      thread.messages.push({ ...msg, status: "delivered" });
      thread.last_body = msg.body; thread.last_at = msg.created_at;
    });
    setThreads(prev => {
      const merged = [...prev];
      threadMap.forEach((thread, id) => {
        const idx = merged.findIndex(t => t.thread_id === id);
        if (idx >= 0) merged[idx] = { ...merged[idx], messages: [...merged[idx].messages, ...thread.messages] };
        else merged.push(thread);
      });
      return merged.sort((a, b) => { if (a.pinned && !b.pinned) return -1; if (!a.pinned && b.pinned) return 1; return new Date(b.last_at).getTime() - new Date(a.last_at).getTime(); });
    });
  }, [dbMessages]);

  // Real-time subscription
  useEffect(() => {
    if (!user) return;
    const channel = supabase.channel("parent-messages-realtime")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "parent_messages", filter: `recipient_id=eq.${user.id}` }, (payload) => {
        const msg = payload.new as Message;
        setThreads(prev => {
          const existing = prev.find(t => t.thread_id === msg.thread_id);
          if (existing) return prev.map(t => t.thread_id === msg.thread_id ? { ...t, messages: [...t.messages, { ...msg, status: "delivered" }], last_body: msg.body, last_at: msg.created_at, unread: true } : t);
          return [{ thread_id: msg.thread_id, sender_name: msg.sender_name, sender_role: msg.sender_role, subject: msg.subject, last_body: msg.body, last_at: msg.created_at, unread: true, messages: [{ ...msg, status: "delivered" }] }, ...prev];
        });
        toast.success(`New message from ${msg.sender_name}`);
      }).subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user]);

  useEffect(() => { if (activeThread) setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 50); }, [activeThread?.messages.length]);

  const sendReply = async () => {
    if (!reply.trim() || !activeThread || !user) return;
    const tempId = `temp-${Date.now()}`;
    // Demo threads use non-UUID thread_ids; generate a real UUID for DB persistence
    const isDemoThread = !activeThread.thread_id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
    const dbThreadId = isDemoThread ? crypto.randomUUID() : activeThread.thread_id;
    const newMsg: Message = { id: tempId, thread_id: activeThread.thread_id, sender_id: user.id, sender_name: "You", sender_role: "parent", recipient_id: null, student_id: null, subject: null, body: reply.trim(), read_at: null, created_at: new Date().toISOString(), status: "sending" };
    const updated = { ...activeThread, messages: [...activeThread.messages, newMsg], last_body: reply.trim(), last_at: newMsg.created_at };
    setActiveThread(updated);
    setThreads(prev => prev.map(t => t.thread_id === activeThread.thread_id ? updated : t));
    setReply("");
    try {
      const { data, error } = await supabase.from("parent_messages").insert({ thread_id: dbThreadId, sender_id: user.id, sender_name: user.user_metadata?.full_name ?? "Parent", sender_role: "parent", body: reply.trim(), subject: activeThread.subject }).select().single();
      if (error) throw error;
      if (data) {
        setThreads(prev => prev.map(t => t.thread_id === activeThread.thread_id ? { ...t, thread_id: dbThreadId, messages: t.messages.map(m => m.id === tempId ? { ...m, id: data.id, status: "sent" } : m) } : t));
        setActiveThread(prev => prev ? { ...prev, thread_id: dbThreadId, messages: prev.messages.map(m => m.id === tempId ? { ...m, id: data.id, status: "sent" } : m) } : null);
      }
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to send message");
      setThreads(prev => prev.map(t => t.thread_id === activeThread.thread_id ? { ...t, messages: t.messages.map(m => m.id === tempId ? { ...m, status: "failed" } : m) } : t));
      setActiveThread(prev => prev ? { ...prev, messages: prev.messages.map(m => m.id === tempId ? { ...m, status: "failed" } : m) } : null);
    }
  };

  const filteredThreads = threads.filter(t => {
    if (t.archived) return false;
    const matchesSearch = !search || t.sender_name.toLowerCase().includes(search.toLowerCase()) || t.subject?.toLowerCase().includes(search.toLowerCase()) || t.last_body.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = activeCategory === "all" || t.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const stats = { total: threads.length, unread: threads.filter(t => t.unread).length, starred: threads.filter(t => t.starred).length };
  const firstName = (user?.user_metadata?.full_name ?? "Jane").split(" ")[0];

  // AI Daily Summary
  const dailySummary = [
    "Emma attended every class today.",
    "She borrowed two library books.",
    "Lunch was purchased at 12:18 PM.",
    "Science homework has been assigned.",
    "Bus Route B is expected five minutes late.",
    "No health or behaviour concerns reported today.",
  ];

  // Approve modal + Pay Now state
  const [showApprove, setShowApprove] = useState(false);
  const [showPayNow, setShowPayNow] = useState(false);
  const [approveStep, setApproveStep] = useState<"review" | "signing" | "done">("review");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingAttachment, setPendingAttachment] = useState<{ name: string; url: string; type: string; size: string } | null>(null);

  const handleAttach = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10MB)"); return; }
    const sizeStr = file.size > 1024 * 1024 ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : `${Math.round(file.size / 1024)} KB`;
    // Preview images, otherwise just show file info
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => setPendingAttachment({ name: file.name, url: reader.result as string, type: "image", size: sizeStr });
      reader.readAsDataURL(file);
    } else {
      setPendingAttachment({ name: file.name, url: "", type: "file", size: sizeStr });
    }
    toast.success(`Attached: ${file.name}`);
  };

  const startCall = (video: boolean) => {
    toast.success(`Starting ${video ? "video" : "voice"} call…`, { description: "Connecting you with the school office" });
  };

  const handleApprove = () => {
    setApproveStep("signing");
    setTimeout(() => setApproveStep("done"), 1800);
    setTimeout(() => { setShowApprove(false); setApproveStep("review"); toast.success("Consent form approved & signed digitally"); }, 2800);
  };

  // Needs Attention items
  const needsAttention = [
    { id: "a1", title: "Sign Science Trip Consent", due: "Due Today", icon: Shield, color: C.red, action: "Approve", urgent: true, category: "academic", onAction: () => setShowApprove(true) },
    { id: "a2", title: "Outstanding Fees Reminder", due: "Due Monday", icon: CreditCard, color: C.amber, action: "Pay Now", category: "finance", onAction: () => setShowPayNow(true) },
  ];

  // Today's Updates
  const todayUpdates = [
    { id: "u1", text: "Emma arrived safely at school.", time: "07:43", icon: CheckCircle2, color: C.green, category: "transport" },
    { id: "u2", text: "Science homework assigned.", time: "10:22", icon: BookOpen, color: C.purple, category: "academic" },
    { id: "u3", text: "Lunch purchased.", time: "12:19", icon: CreditCard, color: C.orange, category: "finance" },
    { id: "u4", text: "Football practice cancelled.", time: "14:00", icon: AlertCircle, color: C.red, category: "announcements" },
    { id: "u5", text: "Bus departed school.", time: "16:30", icon: Bus, color: C.teal, category: "transport" },
  ];

  // Message intelligence
  const msgIntel = [
    { label: "This week", value: "18 messages", icon: InboxIcon, color: C.accent },
    { label: "Action required", value: "3 need response", icon: AlertTriangle, color: C.red },
    { label: "Completed", value: "15 resolved", icon: CheckCircle2, color: C.green },
    { label: "Avg response", value: "12 minutes", icon: Clock, color: C.teal },
  ];

  const activeMessages = activeThread?.messages ?? [];
  const greeting = (() => { const h = new Date().getHours(); if (h < 12) return { text: "Good morning", icon: Sun, phase: "Morning" }; if (h < 17) return { text: "Good afternoon", icon: Coffee, phase: "Afternoon" }; return { text: "Good evening", icon: Moon, phase: "Evening" }; })();
  const GreetingIcon = greeting.icon;

  return (
    <div className="space-y-6 sm:space-y-8 max-w-5xl mx-auto">
      {/* ═════════ AMBIENT HERO ═════════ */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: EASE }}
        className="relative overflow-hidden rounded-[24px] border shadow-float"
        style={{ background: INBOX_GRADIENT, borderColor: "rgba(255,255,255,0.15)" }}
      >
        <motion.div className="absolute -right-12 -top-12 h-44 w-44 rounded-full opacity-20" style={{ background: "radial-gradient(circle, white, transparent 70%)" }} animate={{ scale: [1, 1.2, 1], opacity: [0.15, 0.25, 0.15] }} transition={{ duration: 6, repeat: Infinity }} />
        <motion.div className="absolute -left-16 -bottom-16 h-52 w-52 rounded-full opacity-10" style={{ background: "radial-gradient(circle, #D4B89A, transparent 70%)" }} animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 8, repeat: Infinity, delay: 2 }} />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "20px 20px" }} />

        <div className="relative px-6 sm:px-10 py-8 sm:py-10">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-12 w-12 rounded-2xl flex items-center justify-center bg-white/15 backdrop-blur-md">
              <GreetingIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/50">Family Inbox · {greeting.phase}</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">{greeting.text}, {firstName}</h2>
            </div>
          </div>

          <p className="text-lg text-white/70 max-w-lg">
            {stats.unread > 0 ? `${stats.unread} conversations need your attention.` : "Everything across your family is up to date."}
          </p>

          {/* Quick stats */}
          <div className="flex flex-wrap gap-3 mt-6">
            {[
              { label: "Unread", value: stats.unread, color: "text-amber-400", icon: Bell },
              { label: "Starred", value: stats.starred, color: "text-yellow-400", icon: Star },
              { label: "Total", value: stats.total, color: "text-emerald-400", icon: InboxIcon },
            ].map((s, i) => (
              <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.08 }} className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl bg-white/10 backdrop-blur-md border border-white/10">
                <s.icon className={`h-4 w-4 ${s.color}`} />
                <span className="text-lg font-bold text-white">{s.value}</span>
                <span className="text-xs text-white/50">{s.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* ═════════ AI DAILY SUMMARY — glassmorphic ═════════ */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.5 }}>
        <div className="relative overflow-hidden rounded-[24px] border bg-white shadow-premium" style={{ borderColor: C.cardBorder }}>
          <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: INBOX_GRADIENT }} />
          <div className="px-5 sm:px-6 py-4 border-b border-[#E2D6C0]/60 flex items-center gap-3">
            <div className="relative h-10 w-10 rounded-[12px] flex items-center justify-center" style={{ background: C.purpleLight }}>
              <Sparkles className="h-5 w-5" style={{ color: C.purple }} />
              <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white" style={{ background: C.green }} />
            </div>
            <div>
              <h3 className="text-base font-semibold tracking-tight" style={{ color: C.text }}>Today's AI Summary</h3>
              <p className="text-xs" style={{ color: C.textMuted }}>Auto-generated daily briefing · 6 items</p>
            </div>
            <div className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-lg" style={{ background: C.greenLight }}>
              <span className="h-2 w-2 rounded-full animate-pulse" style={{ background: C.green }} />
              <span className="text-[10px] font-semibold" style={{ color: C.green }}>All Clear</span>
            </div>
          </div>
          <div className="p-5 sm:p-6">
            <div className="grid gap-2.5 sm:grid-cols-2">
              {dailySummary.map((line, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.15 + i * 0.06 }} className="flex items-start gap-3 p-3 rounded-[14px] transition-colors hover:bg-[#EDE4D4]/50">
                  <div className="h-6 w-6 rounded-full flex items-center justify-center shrink-0 mt-0.5" style={{ background: C.greenLight }}>
                    <CheckCircle2 className="h-3.5 w-3.5" style={{ color: C.green }} />
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: C.textMid }}>{line}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* ═════════ NEEDS ATTENTION — priority cards ═════════ */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.5 }}>
        <div className="flex items-center gap-2 mb-4">
          <div className="h-8 w-8 rounded-xl flex items-center justify-center" style={{ background: C.redLight }}>
            <AlertTriangle className="h-4 w-4" style={{ color: C.red }} />
          </div>
          <h3 className="text-lg font-semibold tracking-tight" style={{ color: C.text }}>Needs Your Attention</h3>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: C.redLight, color: C.red }}>{needsAttention.length}</span>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {needsAttention.map((item, i) => {
            const catConfig = CATEGORY_CONFIG[item.category] ?? CATEGORY_CONFIG.inbox;
            return (
              <motion.div key={item.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.08 }} whileHover={{ y: -3 }} className="group relative flex items-center gap-3 p-4 rounded-[18px] border bg-white cursor-pointer transition-all overflow-hidden" style={{ borderColor: item.urgent ? `${C.red}40` : C.cardBorder }}>
                <div className="absolute left-0 top-3 bottom-3 w-1 rounded-full" style={{ background: item.color }} />
                <div className="h-11 w-11 rounded-[12px] flex items-center justify-center shrink-0 ml-1 transition-transform group-hover:scale-110" style={{ background: `${item.color}15` }}>
                  <item.icon className="h-5 w-5" style={{ color: item.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold tracking-tight" style={{ color: C.text }}>{item.title}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-xs" style={{ color: item.urgent ? C.red : C.textMuted }}>{item.due}</p>
                    <span className="h-1 w-1 rounded-full" style={{ background: C.textLight }} />
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: catConfig.bg, color: catConfig.color }}>{item.category}</span>
                  </div>
                </div>
                <button onClick={item.onAction} className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full text-white shrink-0 transition-transform group-hover:scale-105" style={{ background: item.color }}>
                  {item.action} <ArrowRight className="w-3 h-3" />
                </button>
              </motion.div>
            );
          })}
        </div>
      </motion.div>

      {/* ═════════ TODAY'S UPDATES — visual timeline ═════════ */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.5 }}>
        <div className="rounded-[24px] border bg-white shadow-premium overflow-hidden" style={{ borderColor: C.cardBorder }}>
          <div className="px-5 sm:px-6 py-4 border-b border-[#E2D6C0]/60 flex items-center gap-3">
            <div className="h-10 w-10 rounded-[12px] flex items-center justify-center" style={{ background: C.accentLight }}>
              <Bell className="h-5 w-5" style={{ color: C.accent }} />
            </div>
            <div>
              <h3 className="text-base font-semibold tracking-tight" style={{ color: C.text }}>Today's Updates</h3>
              <p className="text-xs" style={{ color: C.textMuted }}>{todayUpdates.length} activities logged</p>
            </div>
          </div>
          <div className="p-5 sm:p-6">
            <div className="relative pl-8">
              <div className="absolute left-3 top-1 bottom-1 w-px" style={{ background: C.border }} />
              {todayUpdates.map((upd, i) => {
                const catConfig = CATEGORY_CONFIG[upd.category] ?? CATEGORY_CONFIG.inbox;
                return (
                  <motion.div key={upd.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 + i * 0.06 }} whileHover={{ x: 4 }} className="relative flex items-center gap-3 pb-4 last:pb-0 cursor-pointer">
                    <div className="absolute -left-[20px] w-5 h-5 rounded-full flex items-center justify-center transition-transform hover:scale-125" style={{ background: upd.color, boxShadow: "0 0 0 3px white" }}>
                      <upd.icon className="w-2.5 h-2.5 text-white" />
                    </div>
                    <span className="text-xs font-mono font-semibold w-10 shrink-0" style={{ color: C.textMuted }}>{upd.time}</span>
                    <p className="text-sm font-medium flex-1" style={{ color: C.text }}>{upd.text}</p>
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: catConfig.bg, color: catConfig.color }}>{upd.category}</span>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </motion.div>

      {/* ═════════ MESSAGE INTELLIGENCE ═════════ */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.5 }}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {msgIntel.map((intel, i) => (
            <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.3 + i * 0.06 }} whileHover={{ y: -3 }} className="flex flex-col items-center gap-2 p-4 rounded-[16px] border bg-white" style={{ borderColor: C.cardBorder }}>
              <div className="h-10 w-10 rounded-[12px] flex items-center justify-center" style={{ background: `${intel.color}15` }}>
                <intel.icon className="h-5 w-5" style={{ color: intel.color }} />
              </div>
              <p className="text-base font-bold" style={{ color: C.text }}>{intel.value}</p>
              <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: C.textMuted }}>{intel.label}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* ═════════ CONVERSATIONS ═════════ */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold tracking-tight" style={{ color: C.text }}>Conversations</h3>
            <span className="text-xs" style={{ color: C.textMuted }}>· {filteredThreads.length} threads</span>
          </div>
          <button onClick={() => setComposing(true)} className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white transition hover:opacity-90" style={{ background: C.accent }}>
            <Plus className="h-3.5 w-3.5" /> Compose
          </button>
        </div>

        {/* Category chips */}
        <div className="flex flex-wrap gap-2 mb-4">
          {["all", "academic", "finance", "health", "transport", "announcements"].map(cat => {
            const catConfig = cat !== "all" ? CATEGORY_CONFIG[cat] : null;
            return (
              <button key={cat} onClick={() => setActiveCategory(cat)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold capitalize transition" style={activeCategory === cat ? { background: catConfig?.color ?? C.accent, color: "white" } : { background: "white", color: C.textMuted, border: `1px solid ${C.cardBorder}` }}>
                {catConfig && <catConfig.icon className="h-3 w-3" />}
                {cat === "all" ? "All" : cat}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: C.textMuted }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search conversations…" className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm focus:outline-none" style={{ border: `1px solid ${C.cardBorder}`, background: C.card, color: C.text }} />
        </div>

        {/* Thread list */}
        <div className="space-y-3">
          {filteredThreads.map((thread, i) => {
            const config = ROLE_CONFIG[thread.sender_role] ?? ROLE_CONFIG.teacher;
            const catConfig = thread.category ? CATEGORY_CONFIG[thread.category] : null;
            const isHovered = hoveredThread === thread.thread_id;
            return (
              <motion.button
                key={thread.thread_id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                whileHover={{ y: -2 }}
                onHoverStart={() => setHoveredThread(thread.thread_id)}
                onHoverEnd={() => setHoveredThread(null)}
                onClick={() => setActiveThread(thread)}
                className="w-full text-left flex items-start gap-3 p-4 rounded-[18px] border bg-white transition-all relative overflow-hidden"
                style={{ borderColor: thread.unread ? `${config.color}40` : C.cardBorder, boxShadow: thread.unread ? `0 4px 12px -4px ${config.color}20` : "0 1px 3px rgba(0,0,0,0.03)" }}
              >
                {thread.unread && <div className="absolute left-0 top-3 bottom-3 w-1 rounded-full" style={{ background: config.color }} />}
                {thread.pinned && <Pin className="absolute top-2 right-2 h-3 w-3" style={{ color: C.amber }} />}
                <Avatar name={thread.sender_name} role={thread.sender_role} size={48} online={thread.unread} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold truncate" style={{ color: C.text }}>{thread.sender_name}</p>
                    <span className="text-[10px] shrink-0" style={{ color: C.textMuted }}>{timeAgo(thread.last_at)}</span>
                  </div>
                  {thread.subject && <p className="text-xs font-medium truncate mt-0.5" style={{ color: config.color }}>{thread.subject}</p>}
                  <p className="text-xs truncate mt-1" style={{ color: C.textMuted }}>{thread.last_body}</p>
                  <div className="flex items-center gap-2 mt-2">
                    {thread.starred && <Star className="w-3 h-3 fill-amber-400 text-amber-400" />}
                    {thread.unread && <span className="w-2 h-2 rounded-full" style={{ background: config.color }} />}
                    {catConfig && <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded" style={{ background: catConfig.bg, color: catConfig.color }}><catConfig.icon className="h-2.5 w-2.5" />{thread.category}</span>}
                    {thread.priority === "urgent" && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: C.redLight, color: C.red }}>URGENT</span>}
                    {thread.priority === "important" && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: C.amberLight, color: C.amber }}>IMPORTANT</span>}
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* ── Conversation Detail Modal ── */}
      <AnimatePresence>
        {activeThread && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
            style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(16px)" }}
            onClick={() => setActiveThread(null)}>
            <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }} transition={springTransition}
              className="w-full sm:max-w-2xl h-[90vh] sm:h-[85vh] rounded-t-[24px] sm:rounded-[24px] overflow-hidden bg-white flex flex-col"
              onClick={e => e.stopPropagation()}>
              {/* Chat header */}
              <div className="flex items-center gap-3 px-5 py-4 border-b shrink-0" style={{ borderColor: C.cardBorder, background: C.bg }}>
                <button onClick={() => setActiveThread(null)} className="lg:hidden p-2 rounded-xl hover:bg-[#E2D6C0]"><ArrowLeft className="w-5 h-5" style={{ color: C.textMid }} /></button>
                <Avatar name={activeThread.sender_name} role={activeThread.sender_role} size={40} online />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold" style={{ color: C.text }}>{activeThread.sender_name}</p>
                  <p className="text-xs" style={{ color: C.textMuted }}>{ROLE_CONFIG[activeThread.sender_role]?.label ?? "Staff"}{activeThread.subject && ` · ${activeThread.subject}`}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => startCall(false)} className="h-9 w-9 rounded-xl flex items-center justify-center border transition-colors hover:bg-[#EDE4D4]" style={{ borderColor: C.border }}><Phone className="w-4 h-4" style={{ color: C.textMid }} /></button>
                  <button onClick={() => startCall(true)} className="h-9 w-9 rounded-xl flex items-center justify-center border transition-colors hover:bg-[#EDE4D4]" style={{ borderColor: C.border }}><Video className="w-4 h-4" style={{ color: C.textMid }} /></button>
                </div>
              </div>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-5 space-y-4" style={{ background: `linear-gradient(180deg, ${C.bg} 0%, #FFFFFF 100%)` }}>
                {activeMessages.map((msg, i) => {
                  const isMe = msg.sender_role === "parent";
                  const prev = activeMessages[i - 1];
                  const showAvatar = !prev || prev.sender_id !== msg.sender_id || isMe;
                  return <MessageBubble key={msg.id} msg={msg} isMe={isMe} showAvatar={showAvatar} />;
                })}
                <div ref={endRef} />
              </div>
              {/* Input */}
              <div className="p-4 border-t shrink-0" style={{ borderColor: C.cardBorder, background: C.card }}>
                {pendingAttachment && (
                  <div className="mb-2 flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
                    {pendingAttachment.type === "image" ? (
                      <img src={pendingAttachment.url} alt="" className="h-10 w-10 rounded-lg object-cover" />
                    ) : (
                      <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ background: C.accentLight }}><FileText className="h-5 w-5" style={{ color: C.accent }} /></div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate" style={{ color: C.text }}>{pendingAttachment.name}</p>
                      <p className="text-[10px]" style={{ color: C.textMuted }}>{pendingAttachment.size}</p>
                    </div>
                    <button onClick={() => setPendingAttachment(null)} className="p-1 rounded-lg hover:bg-[#E2D6C0]"><X className="h-3.5 w-3.5" style={{ color: C.textMuted }} /></button>
                  </div>
                )}
                <input ref={fileInputRef} type="file" accept="image/*,.pdf,.doc,.docx" className="hidden" onChange={handleAttach} />
                <div className="flex items-end gap-2">
                  <button onClick={() => fileInputRef.current?.click()} className="h-10 w-10 rounded-xl flex items-center justify-center border shrink-0 transition-colors hover:bg-[#EDE4D4]" style={{ borderColor: C.border, color: C.textMuted }}><Paperclip className="w-5 h-5" /></button>
                  <textarea ref={inputRef} value={reply} onChange={e => setReply(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }} placeholder="Type a message…" rows={1} className="flex-1 px-4 py-3 rounded-2xl text-sm resize-none focus:outline-none" style={{ background: C.bg, color: C.text }} />
                  <button onClick={sendReply} disabled={!reply.trim()} className="h-11 w-11 rounded-xl flex items-center justify-center text-white shrink-0 disabled:opacity-40" style={{ background: reply.trim() ? C.accent : C.border }}>
                    {reply.trim() ? <Send className="w-5 h-5" /> : <Zap className="w-5 h-5" style={{ color: C.textMuted }} />}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Compose Modal ── */}
      <AnimatePresence>
        {composing && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.4)", backdropFilter: "blur(16px)" }}
            onClick={() => setComposing(false)}>
            <motion.div initial={{ scale: 0.92, y: 24, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.92, y: 24, opacity: 0 }} transition={springTransition}
              className="w-full max-w-lg rounded-[24px] bg-white shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: C.cardBorder, background: C.bg }}>
                <h3 className="text-base font-semibold tracking-tight" style={{ color: C.text }}>New Message</h3>
                <button onClick={() => setComposing(false)} className="p-2 rounded-xl hover:bg-[#E2D6C0]"><X className="w-4 h-4" style={{ color: C.textMuted }} /></button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-tight" style={{ color: C.textMuted }}>Recipient</label>
                  <input value={composeTo} onChange={e => setComposeTo(e.target.value)} placeholder="Search teachers, staff…" className="w-full mt-1 px-4 py-3 rounded-xl text-sm focus:outline-none" style={{ border: `1px solid ${C.border}`, background: C.bg, color: C.text }} />
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-tight" style={{ color: C.textMuted }}>Subject</label>
                  <input value={composeSubject} onChange={e => setComposeSubject(e.target.value)} placeholder="What's this about?" className="w-full mt-1 px-4 py-3 rounded-xl text-sm focus:outline-none" style={{ border: `1px solid ${C.border}`, background: C.bg, color: C.text }} />
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-tight" style={{ color: C.textMuted }}>Message</label>
                  <textarea value={composeBody} onChange={e => setComposeBody(e.target.value)} placeholder="Type your message…" rows={5} className="w-full mt-1 px-4 py-3 rounded-xl text-sm resize-none focus:outline-none" style={{ border: `1px solid ${C.border}`, background: C.bg, color: C.text }} />
                </div>
              </div>
              <div className="px-6 py-4 border-t flex justify-end gap-3" style={{ borderColor: C.cardBorder, background: C.bg }}>
                <button onClick={() => setComposing(false)} className="px-4 py-2.5 rounded-xl text-sm font-semibold" style={{ background: C.card, color: C.textMuted }}>Cancel</button>
                <button disabled={!composeBody.trim() || !composeSubject.trim()} onClick={async () => {
                  if (!user) { toast.error("Sign in required"); return; }
                  try {
                    const newThreadId = crypto.randomUUID();
                    const { error } = await supabase.from("parent_messages").insert({ thread_id: newThreadId, sender_id: user.id, sender_name: user.user_metadata?.full_name ?? "Parent", sender_role: "parent", subject: composeSubject.trim(), body: composeBody.trim() });
                    if (error) throw error;
                    toast.success("Message sent"); setComposing(false); setComposeSubject(""); setComposeBody(""); setComposeTo("");
                    qc.invalidateQueries({ queryKey: ["parent_messages"] });
                  } catch (e: any) { toast.error(e?.message || "Failed to send"); }
                }} className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40" style={{ background: C.accent }}>
                  <Send className="w-4 h-4 inline mr-1.5" />Send
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Approve Modal — stunning consent signing ── */}
      <AnimatePresence>
        {showApprove && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(16px)" }}
            onClick={() => { if (approveStep === "review") setShowApprove(false); }}
          >
            <motion.div
              initial={{ scale: 0.9, y: 30, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.9, y: 30, opacity: 0 }}
              transition={{ type: "spring", stiffness: 350, damping: 28 }}
              className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {approveStep === "review" && (
                <>
                  <div className="relative px-6 py-5 overflow-hidden" style={{ background: "linear-gradient(135deg, #0B2A1F, #00897B)" }}>
                    <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full opacity-20" style={{ background: "white" }} />
                    <div className="relative flex items-center gap-3">
                      <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.2)" }}>
                        <Shield className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-white/60">Consent Form</p>
                        <h3 className="text-lg font-bold text-white" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Science Trip Permission</h3>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="mb-4 p-4 rounded-2xl" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="h-4 w-4" style={{ color: C.red }} />
                        <p className="text-xs font-bold" style={{ color: C.red }}>Action Required Today</p>
                      </div>
                      <p className="text-sm leading-relaxed" style={{ color: C.textMid }}>
                        Greenhill Academy requests your permission for Emma Nakamya to attend a field trip to the Uganda Museum on July 30, 2025. Transportation and supervision will be provided.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-5">
                      <div className="p-3 rounded-xl" style={{ background: C.bg }}>
                        <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: C.textMuted }}>Student</p>
                        <p className="text-sm font-semibold" style={{ color: C.text }}>Emma Nakamya</p>
                      </div>
                      <div className="p-3 rounded-xl" style={{ background: C.bg }}>
                        <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: C.textMuted }}>Trip Date</p>
                        <p className="text-sm font-semibold" style={{ color: C.text }}>Jul 30, 2025</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button onClick={() => setShowApprove(false)} className="flex-1 py-3 rounded-xl text-sm font-bold" style={{ background: C.bg, color: C.textMid }}>Decline</button>
                      <button onClick={handleApprove} className="flex-1 py-3 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2" style={{ background: "linear-gradient(135deg, #C4A882, #D4B89A)", boxShadow: "0 4px 14px rgba(52,199,89,0.35)" }}>
                        <PenTool className="h-4 w-4" /> Approve & Sign
                      </button>
                    </div>
                  </div>
                </>
              )}
              {approveStep === "signing" && (
                <div className="p-16 text-center">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="h-16 w-16 rounded-full mx-auto mb-5 flex items-center justify-center"
                    style={{ border: "3px solid #E2D6C0", borderTopColor: "#00897B" }}
                  />
                  <p className="text-base font-bold" style={{ color: C.text, fontFamily: '"Playfair Display", Georgia, serif' }}>Digitally signing…</p>
                  <p className="text-xs mt-1" style={{ color: C.textMuted }}>Applying your cryptographic signature</p>
                </div>
              )}
              {approveStep === "done" && (
                <div className="p-16 text-center">
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200 }} className="h-20 w-20 rounded-full mx-auto mb-5 flex items-center justify-center" style={{ background: "rgba(52,199,89,0.1)" }}>
                    <CheckCircle2 className="h-10 w-10" style={{ color: C.green }} />
                  </motion.div>
                  <p className="text-lg font-bold" style={{ color: C.text, fontFamily: '"Playfair Display", Georgia, serif' }}>Signed & Approved</p>
                  <p className="text-xs mt-1" style={{ color: C.textMuted }}>Consent form submitted to Greenhill Academy</p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Pay Now Modal — TriSphere Pay ── */}
      <AnimatePresence>
        {showPayNow && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(16px)" }}
            onClick={() => setShowPayNow(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 30, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.9, y: 30, opacity: 0 }}
              transition={{ type: "spring", stiffness: 350, damping: 28 }}
              className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative px-6 py-5 overflow-hidden" style={{ background: "linear-gradient(135deg, #0B2A1F, #00897B)" }}>
                <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full opacity-20" style={{ background: "white" }} />
                <div className="relative flex items-center gap-3">
                  <div className="h-12 w-12 rounded-2xl flex items-center justify-center" style={{ background: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)", border: "1px solid rgba(255,255,255,0.2)" }}>
                    <CreditCard className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-white/60">Outstanding Fees</p>
                    <h3 className="text-lg font-bold text-white" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Pay via TriSphere Pay</h3>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="mb-4 p-4 rounded-2xl" style={{ background: C.bg, border: `1px solid ${C.border}` }}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold" style={{ color: C.textMuted }}>Amount Due</span>
                    <span className="text-lg font-bold" style={{ color: C.text }}>UGX 1,850,000</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold" style={{ color: C.textMuted }}>Student</span>
                    <span className="text-sm font-semibold" style={{ color: C.text }}>Emma Nakamya</span>
                  </div>
                </div>
                <p className="text-xs mb-4" style={{ color: C.textMuted }}>Choose a payment method to continue with TriSphere Pay.</p>
                <TriSpherePayTopUp
                  walletId={undefined}
                  students={[{ id: "demo", full_name: "Emma Nakamya", initials: "EN", color_hex: "#00897B" }]}
                  fees={[{ student_id: "demo", total_ugx: 1850000, paid_ugx: 0 }]}
                  trigger={
                    <button className="w-full py-3.5 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2" style={{ background: "linear-gradient(135deg, #0B2A1F, #00897B)", boxShadow: "0 4px 14px rgba(11,42,31,0.3)" }}>
                      <CreditCard className="h-4 w-4" /> Pay UGX 1,850,000
                    </button>
                  }
                />
                <button onClick={() => setShowPayNow(false)} className="w-full mt-2 py-2.5 rounded-xl text-sm font-semibold" style={{ color: C.textMuted }}>Cancel</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
